import {
  Option,
  ProviderFiltersType,
  ProviderSearchFilters,
} from '../../models/ProviderSearch';
import { FilterCollectionModel, VirtualCareOptions } from '../Constants';

/**
 * Checks if Priority Scheduling (BCC) filter is currently active.
 *
 * @param selectedFilters - The current selected filters
 * @returns true if Priority Scheduling is active, false otherwise
 */
export const isPrioritySchedulingActive = (
  selectedFilters: ProviderSearchFilters
): boolean => {
  const bccFilter =
    selectedFilters?.[FilterCollectionModel.PRIORITY_SCHEDULING];

  if (bccFilter == null) {
    return false;
  } else if (typeof bccFilter === 'boolean') {
    return bccFilter;
  } else if (Array.isArray(bccFilter)) {
    return bccFilter.length > 0;
  } else if (typeof bccFilter === 'object' && 'value' in bccFilter) {
    return (bccFilter as Option).value === true;
  }

  return false;
};

/**
 * Applies BCC (Priority Scheduling) filter auto-select logic to the selected filters.
 * When BCC is enabled:
 *   - Automatically selects "Next 7 days" in the Upcoming Availability filter
 *   - Automatically selects "Offers virtual visits" in the Virtual Care Options filter
 * When BCC is disabled:
 *   - Automatically deselects "Next 7 days" from the Upcoming Availability filter
 *   - Removes the Virtual Care Options filter selection
 *
 * @param selectedFilters - The current selected filters
 * @param providerFilters - Available provider filters or provider filter list (can be array or object)
 * @param bccFilter - The BCC filter value (can be boolean, Option, array, undefined, or null)
 * @returns Updated filters with BCC auto-select logic applied
 */
export const applyBccAutoSelectLogic = (
  selectedFilters: ProviderSearchFilters,
  providerFilters: ProviderFiltersType[] | any,
  bccFilter: boolean | Option | Option[] | undefined | null
): ProviderSearchFilters => {
  let bccEnabled = false;

  if (bccFilter == null) {
    bccEnabled = false;
  } else if (typeof bccFilter === 'boolean') {
    bccEnabled = bccFilter;
  } else if (Array.isArray(bccFilter)) {
    bccEnabled = bccFilter.length > 0;
  } else if (typeof bccFilter === 'object' && 'value' in bccFilter) {
    bccEnabled = (bccFilter as Option).value === true;
  }
  const updatedFilters = { ...selectedFilters };

  // Handle Upcoming Availability filter
  const upcomingAvailabilityFilter = Array.isArray(providerFilters)
    ? providerFilters.find(
        (filter) => filter.model === FilterCollectionModel.UPCOMING_AVAILABILITY
      )
    : providerFilters?.UpcomingAvailability;

  const sevenDayOptionFromProviderFilters =
    upcomingAvailabilityFilter?.options?.find(
      (option) => String(option.value) === '7'
    );

  const currentUpcomingAvailability =
    selectedFilters[FilterCollectionModel.UPCOMING_AVAILABILITY];
  const sevenDayOptionFromSelectedFilters = Array.isArray(
    currentUpcomingAvailability
  )
    ? currentUpcomingAvailability.find((option) => String(option.value) === '7')
    : undefined;

  const sevenDayOption: Option = sevenDayOptionFromProviderFilters ||
    sevenDayOptionFromSelectedFilters || {
      label: 'Next 7 days',
      value: '7',
      dnt_label: 'Next 7 days',
    };

  if (bccEnabled) {
    updatedFilters[FilterCollectionModel.UPCOMING_AVAILABILITY] = [
      sevenDayOption,
    ];
  } else if (!bccEnabled) {
    if (Array.isArray(currentUpcomingAvailability)) {
      const filtered = currentUpcomingAvailability.filter(
        (option) => String(option.value) !== '7'
      );
      updatedFilters[FilterCollectionModel.UPCOMING_AVAILABILITY] =
        filtered.length > 0 ? filtered : [];
    }
  }

  // Handle Virtual Care Options filter
  const virtualCareOptionsFilter = Array.isArray(providerFilters)
    ? providerFilters.find(
        (filter) => filter.model === FilterCollectionModel.VIRTUAL_CARE_OPTIONS
      )
    : providerFilters?.VirtualCareOptions;

  if (bccEnabled && virtualCareOptionsFilter) {
    // Auto-select "Offers virtual visits" option when BCC is enabled
    const virtualVisitsOption = virtualCareOptionsFilter.options?.find(
      (option) =>
        String(option.value) === VirtualCareOptions.OFFERS_VIRTUAL_VISITS
    );

    if (virtualVisitsOption) {
      updatedFilters[FilterCollectionModel.VIRTUAL_CARE_OPTIONS] = [
        virtualVisitsOption,
      ];
    }
  } else if (!bccEnabled) {
    // Remove Virtual Care Options selection when BCC is disabled
    // Only if the current selection is "Offers virtual visits" (auto-selected by BCC)
    const currentVirtualCareOptions =
      selectedFilters[FilterCollectionModel.VIRTUAL_CARE_OPTIONS];

    if (
      Array.isArray(currentVirtualCareOptions) &&
      currentVirtualCareOptions.length === 1 &&
      String(currentVirtualCareOptions[0]?.value) ===
        VirtualCareOptions.OFFERS_VIRTUAL_VISITS
    ) {
      // Clear the filter if it was auto-selected by BCC
      updatedFilters[FilterCollectionModel.VIRTUAL_CARE_OPTIONS] = [];
    }
  }

  return updatedFilters;
};

export const isFilterDisabledByBcc = (model: string, isBccActive: boolean) =>
  isBccActive &&
  (model === FilterCollectionModel.UPCOMING_AVAILABILITY ||
    model === FilterCollectionModel.EAP_OFFERED ||
    model === FilterCollectionModel.VIRTUAL_CARE_OPTIONS);
